import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PageHeader } from '../components/ui/PageHeader';
import { apiService } from '../services/apiService';

interface LogEntry {
  id: number;
  timestamp: string;
  level: string;
  logger_name: string;
  message: string;
  exception_info: string;
  extra_data: Record<string, any>;
}

export const AdminAPILogsPage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [filters, setFilters] = useState({
    level: '',
    search_request_id: '',
    api_id: '',
    search: ''
  });

  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getAdminAPILogs({
        page: currentPage,
        page_size: pageSize,
        level: filters.level || undefined,
        search_request_id: filters.search_request_id ? parseInt(filters.search_request_id) : undefined,
        api_id: filters.api_id ? parseInt(filters.api_id) : undefined,
        search: filters.search || undefined,
        ordering: '-timestamp'
      });
      setLogs(res.results || []);
      setCount(res.count || 0);
    } catch {
      setLogs([]);
      setCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Application Logs"
        subtitle="WARNING and ERROR logs from searches and API execution. Correlate with Failed API Calls using search_request_id and api_id."
        align="center"
      />

      <Card>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Level</label>
              <Select
                value={filters.level}
                onChange={(e) => { setFilters(f => ({ ...f, level: e.target.value })); setCurrentPage(1); }}
              >
                <option value="">All</option>
                <option value="WARNING">WARNING</option>
                <option value="ERROR">ERROR</option>
                <option value="CRITICAL">CRITICAL</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Search Request ID</label>
              <Input
                type="number"
                placeholder="Filter by search ID..."
                value={filters.search_request_id}
                onChange={(e) => { setFilters(f => ({ ...f, search_request_id: e.target.value })); setCurrentPage(1); }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">API ID</label>
              <Input
                type="number"
                placeholder="Filter by API ID..."
                value={filters.api_id}
                onChange={(e) => { setFilters(f => ({ ...f, api_id: e.target.value })); setCurrentPage(1); }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Search in message</label>
              <Input
                type="text"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setCurrentPage(1); }}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner text="Loading logs..." /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">No logs found.</div>
          ) : (
            <>
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, count)} of {count}
              </div>
              <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar pr-1">
                {logs.map((log) => (
                  <div key={log.id} className="border rounded p-3 bg-gray-50 dark:bg-gray-900 text-sm">
                    <div className="flex flex-wrap gap-2 text-gray-600 dark:text-gray-300 mb-1">
                      <span className="font-mono text-xs">{new Date(log.timestamp).toISOString()}</span>
                      <span className={`font-medium ${log.level === 'ERROR' || log.level === 'CRITICAL' ? 'text-red-600 dark:text-red-400' : 'text-amber-600'}`}>
                        [{log.level}]
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">{log.logger_name}</span>
                      {log.extra_data?.search_request_id && (
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-1 rounded">SR#{log.extra_data.search_request_id}</span>
                      )}
                      {log.extra_data?.api_id && (
                        <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 px-1 rounded">API#{log.extra_data.api_id}</span>
                      )}
                    </div>
                    <div className="text-gray-900 dark:text-white">{log.message}</div>
                    {log.exception_info && (
                      <pre className="mt-2 text-red-700 dark:text-red-300 text-xs whitespace-pre-wrap break-words bg-red-50 dark:bg-red-900/20 p-2 rounded">
                        {log.exception_info}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage * pageSize >= count}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
